import { db } from '../firebase/config';
import { collection, getDocs, doc, setDoc, deleteDoc, query, orderBy, where, getDoc, addDoc } from 'firebase/firestore';
import { Spread } from '../types/spread';

const SPREADS_COLLECTION = 'spreads';

// Yerel resimlerin listesi
const localImages = [
  '/images/tarot/tarot40.jpg',
  '/images/tarot/tarot41.png',
  '/images/tarot/tarot43.jpg',
  '/images/tarot/tarot44.png',
  '/images/tarot/tarot45.jpg'
];

let currentImageIndex = 0;

// Sıradaki resmi al
const getNextImage = () => {
  const image = localImages[currentImageIndex];
  currentImageIndex = (currentImageIndex + 1) % localImages.length;
  return image;
};

// Cache sistemi
const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheItem<any>>();

const isCacheValid = (key: string): boolean => {
  const item = cache.get(key);
  if (!item) return false;
  return Date.now() - item.timestamp < CACHE_DURATION;
};

export const spreadsService = {
  getAll: async (): Promise<Spread[]> => {
    try {
      const cacheKey = 'spreads_all';
      
      // Cache kontrolü
      if (isCacheValid(cacheKey)) {
        return cache.get(cacheKey)!.data;
      }

      const q = query(collection(db, SPREADS_COLLECTION), orderBy('order'));
      const querySnapshot = await getDocs(q);
      const spreads = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Spread));

      // Sonuçları cache'e kaydet
      cache.set(cacheKey, {
        data: spreads,
        timestamp: Date.now()
      });

      return spreads;
    } catch (error) {
      throw error;
    }
  },

  getActive: async (): Promise<Spread[]> => {
    try {
      const cacheKey = 'spreads_active';
      
      // Cache kontrolü
      if (isCacheValid(cacheKey)) {
        return cache.get(cacheKey)!.data;
      }

      // Temporarily using a simpler query while index builds
      const q = query(
        collection(db, SPREADS_COLLECTION),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const spreads = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Spread));

      // Sort in memory instead
      spreads.sort((a, b) => a.order - b.order);

      // Sonuçları cache'e kaydet
      cache.set(cacheKey, {
        data: spreads,
        timestamp: Date.now()
      });

      return spreads;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id: string): Promise<Spread | null> => {
    try {
      const cacheKey = `spread_${id}`;
      
      // Cache kontrolü
      if (isCacheValid(cacheKey)) {
        return cache.get(cacheKey)!.data;
      }

      const docRef = doc(db, SPREADS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const spread = {
        id: docSnap.id,
        ...docSnap.data()
      } as Spread;

      // Sonucu cache'e kaydet
      cache.set(cacheKey, {
        data: spread,
        timestamp: Date.now()
      });

      return spread;
    } catch (error) {
      throw error;
    }
  },

  create: async (spread: Omit<Spread, 'id'>): Promise<Spread> => {
    try {
      // Koleksiyona referans al
      const spreadsRef = collection(db, SPREADS_COLLECTION);
      
      // Yeni doküman oluştur
      const docRef = await addDoc(spreadsRef, {
        ...spread,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Oluşturulan dokümanı al
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Document was not created');
      }

      const newSpread = {
        id: docRef.id,
        ...docSnap.data()
      } as Spread;

      // Cache'i temizle
      cache.clear();

      return newSpread;
    } catch (error) {
      console.error('Error creating spread:', error);
      throw error;
    }
  },

  update: async (id: string, spread: Partial<Spread>): Promise<void> => {
    try {
      const docRef = doc(db, SPREADS_COLLECTION, id);
      await setDoc(docRef, spread, { merge: true });
      cache.clear(); // Cache'i temizle
    } catch (error) {
      throw error;
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      const docRef = doc(db, SPREADS_COLLECTION, id);
      await deleteDoc(docRef);
      cache.clear(); // Cache'i temizle
    } catch (error) {
      throw error;
    }
  },

  reorder: async (spreads: Spread[]): Promise<void> => {
    try {
      // Her bir açılımın sırasını güncelle
      for (const spread of spreads) {
        const docRef = doc(db, SPREADS_COLLECTION, spread.id);
        await setDoc(docRef, { order: spread.order }, { merge: true });
      }
      cache.clear(); // Cache'i temizle
    } catch (error) {
      throw error;
    }
  },

  getNextImage
}; 